<div class="wizard">
  <div class="wizard__head">
    <mat-icon>receipt_long</mat-icon>
    <h2>{{ record ? ('Remisión ' + record.folio + (record.status === 'Borrador' || record.status === 'Incompleto' ? ' (incompleto)' : '')) : 'Nueva remisión' }}</h2>
    <span class="spacer"></span>
    <button mat-stroked-button (click)="saveDraft()"><mat-icon>save</mat-icon>Guardar borrador y salir</button>
    <button mat-stroked-button color="warn" (click)="cancel()"><mat-icon>close</mat-icon>Salir sin guardar</button>
  </div>

  <!-- ── Barra de pasos ─────────────────────────────────────────────── -->
  <div class="steps">
    @for (title of stepTitles; track $index) {
      <button class="steps__item"
              [class.steps__item--active]="step === $index"
              [class.steps__item--done]="step > $index"
              (click)="goToStep($index)">
        <span class="steps__number">{{ $index + 1 }}</span>
        <span class="steps__label">{{ title }}</span>
      </button>
      @if ($index < stepTitles.length - 1) { <span class="steps__sep"></span> }
    }
  </div>

  <!-- ═══ Paso 1: Técnico, hospital y médico ═══════════════════════════ -->
  @if (step === 0) {
    <form [formGroup]="form" class="panel">
      <h3>Folio de la remisión</h3>
      <div class="grid">
        <mat-form-field subscriptSizing="dynamic">
          <mat-label>Folio</mat-label>
          <input matInput formControlName="folio" placeholder="Escribe el folio" autocomplete="off">
        </mat-form-field>
      </div>

      <h3>Técnico Medtronic</h3>
      @if (technicianLocked) {
        <p class="hint">Capturas con tu usuario de técnico: <b>{{ technicianLockedName }}</b></p>
      } @else if (technicianList.length === 0) {
        <p class="hint hint--warn">No hay usuarios con rol <b>Técnico</b>. Da de alta al técnico en Usuarios (o revisa su rol).</p>
      } @else {
        <mat-radio-group formControlName="technician" class="radio-list">
          @for (tech of technicianList; track tech.id) {
            <mat-radio-button [value]="tech.id">{{ tech.name }}</mat-radio-button>
          }
        </mat-radio-group>
      }

      <!-- El hospital va antes que el médico (filtra los médicos y, en el
           paso 2, los folios de cirugía). -->
      <h3>Hospital</h3>
      <div class="grid">
        <app-auto-select label="Hospital" formControlName="place" [options]="scopedPlaces" [labelFn]="placeAliasLabel" (changed)="onPlaceChange()"></app-auto-select>
      </div>

      <h3>Médico</h3>
      <div class="grid">
        <app-auto-select label="Médico (opcional)" formControlName="doctor" [options]="doctorsOfPlace" [allowEmpty]="true"></app-auto-select>
      </div>

      <div class="actions">
        <button mat-raised-button color="accent" type="button"
                [disabled]="!form.get('folio')?.value || !form.get('technician')?.value || !form.get('place')?.value"
                (click)="step = 1">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </form>
  }

  <!-- ═══ Paso 2: Folio de cirugía ═════════════════════════════════════ -->
  @if (step === 1) {
    <form [formGroup]="form" class="panel">
      <div class="grid">
        <app-auto-select label="Folio de cirugía (opcional)" formControlName="intervention" [options]="interventionsOfPlace" [labelFn]="interventionLabel" [allowEmpty]="true" emptyLabel="— Remisión directa, sin cirugía —" (changed)="onInterventionChange()"></app-auto-select>
      </div>
      <p class="hint">Solo aparecen los folios del hospital elegido en el paso 1. Al ligar un folio se toman su médico y técnico (puedes revisarlos en el paso 1). El folio de la remisión es el que capturaste en el paso 1.</p>

      <div class="actions">
        <button mat-button type="button" (click)="step = 0"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" type="button"
                [disabled]="!form.get('place')?.value"
                (click)="step = 2">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </form>
  }

  <!-- ═══ Paso 3: Material (igual que el folio: lectura RFID) ══════════ -->
  @if (step === 2) {
    <div class="panel">
      <p class="hint">Cada pieza que agregues queda <b>apartada</b> (Transito) para esta remisión; si la quitas, regresa a Disponible. El precio sale del contrato y se puede marcar sin cargo.</p>

      <div class="grid">
        <app-auto-select label="Locker del hospital" [(ngModel)]="selectedWarehouse" [options]="warehouseOptions" [disabled]="lines.length > 0"></app-auto-select>
        <mat-button-toggle-group [(ngModel)]="rfidMode" (change)="onRfidModeChange()" class="mode-toggle">
          <mat-button-toggle value="widget"><mat-icon>qr_code_scanner</mat-icon> Widget</mat-button-toggle>
          <mat-button-toggle value="bandeja"><mat-icon>inbox</mat-icon> Bandeja</mat-button-toggle>
          <mat-button-toggle value="anaquel"><mat-icon>shelves</mat-icon> Anaquel</mat-button-toggle>
          <mat-button-toggle value="pistola"><mat-icon>settings_remote</mat-icon> Pistola</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      @if (rfidMode === 'widget') {
        <!-- Lectura uno a uno: el lector escribe la etiqueta y da Enter. -->
        <div class="grid">
          <mat-form-field class="wide"><mat-label>Escanear etiqueta RFID (24 caracteres)</mat-label>
            <input matInput maxlength="24" [(ngModel)]="scanValue" (keyup.enter)="scanTag()" [disabled]="!selectedWarehouse" placeholder="El lector escribe aquí y da Enter">
            <mat-icon matSuffix>qr_code_scanner</mat-icon>
            <mat-hint align="end">{{ (scanValue || '').length }}/24</mat-hint>
          </mat-form-field>
        </div>

        <details #rowAdd1 class="row-add">
          <summary>Agregar material sin etiqueta (por renglón)</summary>
          <div class="grid">
            <app-auto-select label="Insumo (código o descripción)" [(ngModel)]="rowAdd.product" [options]="productsList" [labelFn]="productLabel"></app-auto-select>
            <mat-form-field><mat-label>Lote (opcional)</mat-label><input matInput [(ngModel)]="rowAdd.batch"></mat-form-field>
            <mat-form-field class="narrow"><mat-label>Cantidad</mat-label><input matInput type="number" min="1" step="1" (change)="rowAdd.quantity = toInt(rowAdd.quantity, 1)" [(ngModel)]="rowAdd.quantity"></mat-form-field>
            <button mat-stroked-button [disabled]="!selectedWarehouse" (click)="addByRow()"><mat-icon>add</mat-icon>Apartar</button>
          </div>
                <div class="row-add__close">
          <button mat-button type="button" (click)="rowAdd1.open = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
      </details>
      } @else {
        <!-- Lectura por dispositivo Jetstream: se lee, se revisa QUÉ leyó y se
             seleccionan las piezas con checkbox antes de apartarlas. -->
        <div class="grid">
          <mat-form-field><mat-label>{{ rfidMode === 'pistola' ? 'Dispositivo handheld (p.ej. Handheld-ICU)' : 'Nombre del dispositivo lector' }}</mat-label>
            <input matInput [(ngModel)]="deviceName" [disabled]="reading">
          </mat-form-field>
          <button mat-raised-button color="primary" [disabled]="reading || !selectedWarehouse" (click)="readDevice()">
            <mat-icon>sensors</mat-icon>{{ reading ? 'Leyendo...' : 'Leer ' + rfidMode }}
          </button>
        </div>

        @if (readTags.length > 0) {
          <div class="read-actions">
            <span><b>{{ readTags.length }}</b> etiquetas leídas · {{ selectableTags }} disponibles para cargar</span>
            <span class="spacer"></span>
            <button mat-button (click)="toggleAllTags(true)">Seleccionar todos</button>
            <button mat-button (click)="toggleAllTags(false)">Deseleccionar todos</button>
            <button mat-raised-button color="accent" (click)="addSelectedTags()"><mat-icon>playlist_add_check</mat-icon>Apartar seleccionadas</button>
          </div>
          <table class="list">
            <tr><th></th><th>Etiqueta (EPC)</th><th>Código</th><th>Descripción</th><th>Lote</th><th>Estado</th></tr>
            @for (tag of readTags; track tag.epc) {
              <tr>
                <td>
                  <mat-checkbox [(ngModel)]="tag.selected" [disabled]="!tag.stockItem || tag.already"></mat-checkbox>
                </td>
                <td>{{ tag.epc }}</td>
                <td>{{ tag.stockItem?.code ?? productsObject[tag.stockItem?.product]?.code ?? '—' }}</td>
                <td>{{ tag.stockItem ? (productsObject[tag.stockItem.product]?.description ?? '—') : 'No está en el inventario de este locker' }}</td>
                <td>{{ tag.stockItem?.batch ?? '—' }}</td>
                <td>{{ tag.already ? 'Ya cargada' : (tag.stockItem?.status ?? '—') }}</td>
              </tr>
            }
          </table>
        }
      }

      <table class="list">
        <tr><th>Código</th><th>Descripción</th><th>Lote</th><th>Etiqueta</th><th class="num">Precio unit.</th><th>Sin cargo</th><th>Motivo</th><th></th></tr>
        @for (line of lines; track line.stock) {
          <tr [class.warn-row]="line.sinPrecio && !line.noCharge">
            <td>{{ line.code }}</td>
            <td>{{ line.description }}</td>
            <td>{{ line.batch || '—' }}</td>
            <td>{{ line.tagRfid || 'Sin etiqueta' }}</td>
            <td class="num"><input class="cell cell--num" type="number" min="0" [(ngModel)]="line.unitPrice" [disabled]="line.noCharge"></td>
            <td><mat-checkbox [(ngModel)]="line.noCharge"></mat-checkbox></td>
            <td>
              <mat-form-field class="reason" subscriptSizing="dynamic">
                <mat-select [(ngModel)]="line.reason" [disabled]="!line.noCharge" placeholder="—">
                  <mat-option value="Cortesía">Cortesía</mat-option>
                  <mat-option value="Garantía">Garantía</mat-option>
                  <mat-option value="Reposición">Reposición</mat-option>
                </mat-select>
              </mat-form-field>
            </td>
            <td><button mat-icon-button color="warn" matTooltip="Quitar (regresa a Disponible)" (click)="removeLine($index)"><mat-icon>close</mat-icon></button></td>
          </tr>
        }
      </table>
      @if (lines.length === 0) { <p class="empty">Sin material cargado.</p> }
      <p class="count"><b>{{ lines.length }}</b> piezas apartadas · Total: <b>{{ total | number:'1.2-2' }}</b></p>
      <p class="hint hint--warn" [hidden]="!linesWithoutPrice">Hay piezas sin precio en el contrato (en naranja): captura el precio o márcalas sin cargo.</p>

      <details #rowAdd2 class="row-add" [attr.open]="otherItems.length > 0 ? '' : null">
        <summary>Otros — fuera de catálogo (CFN / piezas / lote)</summary>
        <div class="grid">
          <mat-form-field><mat-label>CFN</mat-label><input matInput [(ngModel)]="otherAdd.cfn"></mat-form-field>
          <mat-form-field class="narrow"><mat-label>Piezas</mat-label><input matInput type="number" min="1" step="1" (change)="otherAdd.pieces = toInt(otherAdd.pieces, 1)" [(ngModel)]="otherAdd.pieces"></mat-form-field>
          <mat-form-field><mat-label>Lote</mat-label><input matInput [(ngModel)]="otherAdd.batch"></mat-form-field>
          <button mat-stroked-button (click)="addOther()"><mat-icon>add</mat-icon>Agregar</button>
        </div>
        @if (otherItems.length > 0) {
          <table class="list">
            <tr><th>CFN</th><th class="num">Piezas</th><th>Lote</th><th></th></tr>
            @for (other of otherItems; track $index) {
              <tr>
                <td>{{ other.cfn }}</td><td class="num">{{ other.pieces }}</td><td>{{ other.batch || '—' }}</td>
                <td><button mat-icon-button color="warn" (click)="removeOther($index)"><mat-icon>close</mat-icon></button></td>
              </tr>
            }
          </table>
        }
              <div class="row-add__close">
          <button mat-button type="button" (click)="rowAdd2.open = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
      </details>

      <div class="actions">
        <button mat-button (click)="step = 1"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" (click)="step = 3">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </div>
  }

  <!-- ═══ Paso 4: Comentarios y cierre ═════════════════════════════════ -->
  @if (step === 3) {
    <form [formGroup]="form" class="panel">
      <mat-form-field class="wide"><mat-label>Comentarios</mat-label><input matInput formControlName="notes"></mat-form-field>

      <div class="summary">
        <p><b>{{ lines.length }}</b> líneas de material
          @if (otherItems.length > 0) { <span> · {{ otherItems.length }} renglones en Otros</span> }
          · Total: <b>{{ total | number:'1.2-2' }}</b></p>
        @if (form.get('intervention')?.value) { <p>Ligada a folio de cirugía</p> } @else { <p>Remisión directa, sin cirugía</p> }
      </div>

      <div class="actions">
        <button mat-button type="button" (click)="step = 2"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" type="button" (click)="step = 4">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </form>
  }

  <!-- ═══ Paso 5: PDF ══════════════════════════════════════════════════ -->
  @if (step === 4) {
    <div class="panel">
      <p class="hint">El PDF lleva el folio <b>{{ record?.folio ?? form.get('folio')?.value ?? '' }}</b>, un QR con ese folio, una línea por cantidad/lote con su precio, la sección Otros y las firmas de técnico y quien recibe. Si todavía no has guardado, al generar el PDF la remisión se guarda primero como borrador para tener folio.</p>

      <div class="summary">
        <p><b>{{ lines.length }}</b> líneas de material
          @if (otherItems.length > 0) { <span> · {{ otherItems.length }} renglones en Otros</span> }
          · Total: <b>{{ total | number:'1.2-2' }}</b></p>
      </div>

      <div class="actions actions--center">
        <button mat-raised-button color="primary" (click)="generatePdf()"><mat-icon>picture_as_pdf</mat-icon>Generar PDF</button>
        <button mat-raised-button color="accent" (click)="saveAndEmit()"><mat-icon>task_alt</mat-icon>Guardar y emitir</button>
      </div>
      @if (pdfGenerated) { <p class="count">PDF generado. Al emitir, la remisión sale del modo borrador.</p> }

      <div class="actions">
        <button mat-button (click)="step = 3"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <span class="spacer"></span>
        <button mat-stroked-button (click)="saveDraft()"><mat-icon>save</mat-icon>Guardar borrador</button>
      </div>
    </div>
  }
</div>
